import {
  BrowserRouter as Router,
  Routes,
  Route,
} from 'react-router-dom';
import { useTheme } from './components/Theme/ThemeProvider';
import { Menu } from './components/Menu/Menu';
import { MobileMenu } from './components/Menu/MobileMenu';
import { Header } from './components/Header/Header';
import { SlideOutPanel } from './components/Statistics/SlideOutPanel';
import { MainContent } from './components/MainContent/MainContent';
import ChatPage from './pages/ChatPage';
import { DisclaimerNote } from './components/DisclaimerNote/DisclaimerNote';
import { ImageModal } from './components/Dialogs/ImageModal';
import { JiraCardModal } from './components/Dialogs/JiraCardModal';
import { WelcomePopup } from './components/Tutorial/WelcomePopup';
import StatisticsPanel from "./components/Statistics/StatisticsPanel";
import { GuidedTutorial } from './components/Tutorial/GuidedTutorial';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearUploadedFiles, clearAnalysisResults, ensureSessionId, resetSessionId, clearTranslationData } from './store/chatSlice';
import { clearAutoDownloadedFiles } from './components/Chat/MessageContentRenderer';
import { setVoiceModeActive, setVoiceModeListening, setVoiceModePaused, setVoiceModeRestarting, setShowInactivityWarning, setInactivityCountdown, setDisconnectedByInactivity } from './store/voiceModeSlice';
import { voiceModeService } from './utils/voiceMode.service';
import logger from './utils/logger';
import { 
  initializeTutorial, 
  showWelcome 
} from './store/tutorialSlice';
import { hasSeenTutorial, markTutorialAsCompleted } from './utils/tutorialStorage';
import { GalleryMode } from './components/PromptGallery/data/galleryTypes';
import { useDeviceDetection } from './hooks/useDeviceDetection';
import { PromptLibrary } from './features/promptLibrary/PromptLibrary';
import { usePromptLibraryConfirmation } from './features/promptLibrary/usePromptLibraryConfirmation';
import { ConfirmationDialog } from './components/Dialogs/ConfirmationDialog';
import './App.css';
import type { RootState } from './store/store';
import { cancelTranscribingMessages, clearaskChatHistory, clearRagHistory, setRagChatType, setSelectedTool, type ChatModeState } from './store/chatModeSlice';
import { getRagByKey, getRagGalleryMode } from './utils/ragConfig';
import { useAppRouting } from './hooks/useAppRouting';
import { USER_BUBBLE_COLORS, setCookie } from './utils/bubbleColors';

function App() {
    // Statistics panel state
    const [showStatsPanel, setShowStatsPanel] = useState(false);
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);
  const { mode, setFontSize, fontSizeLabels } = useTheme();
  const dispatch = useDispatch();
  const chatModeState: ChatModeState = useSelector((state: RootState) => state.chatMode);
  const voiceModeState = useSelector((state: RootState) => state.voiceMode);
  const { isActive: isVoiceModeActive } = voiceModeState;
  const userPreferences = useSelector((state: RootState) => state.userPreferences);

  // Apply fontSize and bubbleColor from backend preferences once the GET resolves.
  // This runs inside ThemeProvider so setFontSize works directly.
  useEffect(() => {
    if (!userPreferences.hasFetched) return;
    if (userPreferences.fontSize) {
      const idx = fontSizeLabels.indexOf(userPreferences.fontSize);
      if (idx !== -1) setFontSize(idx as 0 | 1 | 2);
    }
    if (userPreferences.bubbleColor) {
      const cssValue = USER_BUBBLE_COLORS.find(c => c.name === userPreferences.bubbleColor)?.value ?? userPreferences.bubbleColor;
      localStorage.setItem('user-bubble-color', cssValue);
      setCookie('user-bubble-color', cssValue);
      window.dispatchEvent(new Event('user-bubble-color-change'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferences.hasFetched]);

  // URL => Redux routing sync (hydration, popstate, push-on-state-change)
  const { hydrated } = useAppRouting();

  // Initialize session ID when app loads (ensures unique session per tab)
  useEffect(() => {
    dispatch(ensureSessionId());
  }, [dispatch]);

  // Tutorial state from Redux
  const tutorialState = useSelector((state: RootState) => state.tutorial);
  const { showWelcomePopup } = tutorialState;
  
  // Initialize tutorial on mount
  useEffect(() => {
    const hasCompleted = hasSeenTutorial();
    dispatch(initializeTutorial(hasCompleted));
  }, [dispatch]);
  
  // Save tutorial completion to localStorage when user completes or skips
  useEffect(() => {
    if (tutorialState.hasSeenTutorial) {
      markTutorialAsCompleted();
    }
  }, [tutorialState.hasSeenTutorial]);
  
  const handleShowWhatsNew = useCallback(() => {
    dispatch(showWelcome());
  }, [dispatch]);
  
  // Page routing state
  const [currentPage, setCurrentPage] = useState<'main' | 'chat'>('main');
  const [chatMessage, setChatMessage] = useState<string>('');
  const [chatTools, setChatTools] = useState<string[]>([]);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Device detection using custom hook
  const deviceInfo = useDeviceDetection();
  const { isMobile, isTablet, windowWidth } = deviceInfo;
  
  // Log device info for debugging (remove in production)
  useEffect(() => {
    logger.log('Device Info:', deviceInfo);
  }, [deviceInfo]);

  // Determine if MainContent should be hidden
  const shouldHideMainContent = windowWidth < 600 && !isMobile && !isTablet;

  // Menu width state for responsive disclaimer positioning (persisted across reloads)
  const [menuWidth, setMenuWidth] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('menu-width');
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed)) return parsed; // allow 0 (collapsed) or any valid stored width
      }
    }
    return 90; // medium default
  });

  // Keep localStorage in sync if external code adjusts menuWidth directly (non-drag scenarios)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('menu-width');
      const parsed = saved ? parseInt(saved, 10) : null;
      if (parsed !== menuWidth) {
        localStorage.setItem('menu-width', menuWidth.toString());
      }
    }
  }, [menuWidth]);
  

  // Gallery state management (moved from Gallery component to be shared between Header and Gallery)
  const [current, setCurrent] = useState<{ mode: GalleryMode | null; direction: number }>({ mode: "Ask Chat", direction: 1 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFirstAnimationRunning, setIsFirstAnimationRunning] = useState(false);
  const exitDirectionRef = useRef<number>(1);
  const setExitDirectionRef = useRef<((direction: number) => void) | null>(null);

    // Function to change modes with direction for animation
  const goToModeWithDirection = useCallback((newMode: GalleryMode | null, direction: number) => {
    // Don't set exitDirectionRef here - it should be set by the calling component
    // before this function is called so the exit animation captures it correctly
    // Set animation state when changing modes
    setIsAnimating(true);
    setCurrent({ mode: newMode, direction: direction });
  }, []);


  // Memoize galleryState to avoid unnecessary re-renders
  const galleryState = useMemo(() => ({
    current,
    goToModeWithDirection,
    exitDirectionRef,
    setExitDirectionRef,
    isFirstAnimationRunning,
    setIsFirstAnimationRunning,
    isAnimating,
    setIsAnimating,
  }), [current, goToModeWithDirection, isFirstAnimationRunning, setIsFirstAnimationRunning, isAnimating, setIsAnimating]);

  // Sync gallery mode with chat mode
  useEffect(() => {
    let newGalleryMode: GalleryMode;
    
    // Map chat mode and RAG chat type to gallery mode via ragConfig
    const ragGalleryMode = getRagGalleryMode(chatModeState.ragChatType);
    if (ragGalleryMode) {
      newGalleryMode = ragGalleryMode;
    } else if (chatModeState.mode === 'Ask Chat') {
      newGalleryMode = 'Ask Chat';
    } else if (chatModeState.mode === '3M Knowledge') {
      // Default to 3M Knowledge for Everyday Insights or null
      newGalleryMode = '3M Knowledge';
    } else {
      // Fallback
      newGalleryMode = 'Ask Chat';
    }
    
    if (current.mode !== newGalleryMode) {
      // Update gallery mode without animation when chat mode changes
      setCurrent({ mode: newGalleryMode, direction: 1 });
    }
  }, [chatModeState.mode, chatModeState.ragChatType, current.mode]);

  // Navigate to chat page with a message
  const navigateToChat = useCallback((message: string, tools?: string[]) => {
    setChatMessage(message);
    setChatTools(tools || []);
    setCurrentPage('chat');
  }, []);

  // Navigate back to main page
  const navigateToMain = useCallback(() => {
    // Stop voice mode if active
    if (isVoiceModeActive) {
      logger.log('[App] Navigating to home - stopping voice mode');
      dispatch(cancelTranscribingMessages());
      voiceModeService.disconnect();
      dispatch(setVoiceModeActive(false));
      dispatch(setVoiceModeListening(false));
      dispatch(setVoiceModePaused(false));
      dispatch(setVoiceModeRestarting(false));
      dispatch(setShowInactivityWarning(false));
      dispatch(setInactivityCountdown(0));
      dispatch(setDisconnectedByInactivity(false));
    }
    
    setCurrentPage('main');
    setChatMessage('');
    setChatTools([]);
    // Clear any active tool when returning home
    dispatch(setSelectedTool(null));
    // clear any uploaded files & analysis results so data_analysis starts fresh
    dispatch(clearUploadedFiles());
    dispatch(clearAnalysisResults());
    // Clear voice chat history for both modes
    dispatch(clearaskChatHistory());
    dispatch(clearRagHistory());
    dispatch(clearTranslationData());
    // Always clear the selected RAG when returning home in Ask Chat mode
    if (chatModeState.mode === 'Ask Chat') {
      dispatch(setRagChatType(null));
    }
    // Clear auto-downloaded translation files tracking
    clearAutoDownloadedFiles();
    // Generate a new session ID when returning to home
    dispatch(resetSessionId());
  }, [dispatch, isVoiceModeActive, chatModeState.mode]);

  // Track whether the chat page has any messages (for Header confirmation dialog)
  const [hasChatMessages, setHasChatMessages] = useState(false);
  const handleHasMessagesChange = useCallback((hasMessages: boolean) => {
    setHasChatMessages(hasMessages);
  }, []);

  // Prompt Library confirmation — logic lives in usePromptLibraryConfirmation hook
  const navigateToMainPage = useCallback(() => {
    setCurrentPage('main');
    setChatMessage('');
    setChatTools([]);
  }, []);

  const {
    handlePromptLibrarySelect,
    handleConfirmClearChat,
    handleCancelPromptSelection,
    showPromptConfirmation,
  } = usePromptLibraryConfirmation({
    currentPage,
    hasChatMessages,
    onClosePromptLibrary: () => setShowPromptLibrary(false),
    onNavigateToMainPage: navigateToMainPage,
  });

  // Select global blocking overlay state
  const globalBlocking = useSelector((s: any) => s.loading.globalBlocking);
  // const blockingMessage = useSelector((s: any) => s.loading.blockingMessage); // unused currently

  if (!hydrated) {
    // Optionally, show a splash/loading screen here
    return null;
  }

  // Set global variable for Header to read showStatsPanel state
  if (typeof window !== 'undefined') {
    window.__SHOW_STATS_PANEL__ = showStatsPanel;
  }

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <Header 
        mode={mode} 
        galleryState={galleryState}
        onMenuToggle={() => setMobileMenuOpen(true)}
        isMobileOrTablet={isMobile || isTablet}
        currentPage={currentPage}
        onNavigateHome={navigateToMain}
        isStreaming={chatModeState.isStreaming}
        isVoiceModeActive={isVoiceModeActive}
        hasMessages={hasChatMessages}
        onStatsClick={() => setShowStatsPanel((prev) => !prev)}
      />

      {/* Header height spacer — pushes content below the fixed header (always 80px) */}
      <div style={{ height: '80px', flexShrink: 0 }} aria-hidden="true" />

      {/* Mobile/Tablet Menu - Full Screen Overlay */}
      {(isMobile || isTablet) && (
        <MobileMenu
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          currentPage={currentPage}
          onClearChat={navigateToMain}
          isMobile={isMobile}
          onShowWhatsNew={handleShowWhatsNew}
        />
      )}

      {/* Mobile/Tablet Active RAG indicator bar — shown below header when a RAG is selected */}
      {(isMobile || isTablet) && chatModeState.mode === '3M Knowledge' && chatModeState.ragChatType !== null && (() => {
        const activeRagConfig = getRagByKey(chatModeState.ragChatType);
        if (!activeRagConfig) return null;
        const IconComp = activeRagConfig.iconComponent;
        return (
          <div
            className="flex items-center justify-center gap-2 py-1.5 px-4 z-10"
            style={{
              backgroundColor: 'var(--main-background)',
              borderBottom: '1px solid var(--color-border, rgba(128,128,128,0.2))',
            }}
          >
            <span style={{ color: 'var(--color-text)', opacity: 0.9, display: 'flex', alignItems: 'center' }}>
              <IconComp {...activeRagConfig.iconProps} width={16} height={16} size={16} style={{ color: 'currentColor' }} />
            </span>
            <span
              className="text-xs font-semibold"
              style={{ fontFamily: '3MCircularTT', color: 'var(--color-text)', opacity: 0.9 }}
            >
              {activeRagConfig.label}
            </span>
          </div>
        );
      })()}
      
      <div className="flex flex-1 h-0 overflow-hidden">
        {/* Desktop Menu - Side Panel (hidden on mobile/tablet) */}
        {!isMobile && !isTablet && (
          <Menu 
            currentPage={currentPage}
            onClearChat={navigateToMain}
            width={menuWidth}
            onWidthChange={setMenuWidth}
            onShowWhatsNew={handleShowWhatsNew}
            onPromptLibraryClick={() => setShowPromptLibrary(prev => !prev)}
            isPromptLibraryOpen={showPromptLibrary}
            deviceType="desktop"
          />
        )}
        {/* Slide-out statistics panel — overlays content area to the right of Menu */}
        <SlideOutPanel isOpen={showStatsPanel} onClose={() => setShowStatsPanel(false)} leftOffset={!isMobile && !isTablet ? menuWidth : 0}>
          <StatisticsPanel />
        </SlideOutPanel>
        {currentPage === 'main' && !shouldHideMainContent ? (
          <MainContent
            galleryState={galleryState}
            onNavigateToChat={navigateToChat}
            deviceType={isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'}
            showPromptLibrary={showPromptLibrary}
            onClosePromptLibrary={() => setShowPromptLibrary(false)}
          />
        ) : currentPage === 'main' && shouldHideMainContent ? (
          <div className="flex-1 flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-bg)' }}>
            <div className="text-center">
              {/* For now just throwing in messages so I don't have to worry about formatting on tiny screens */}
              <h2 className="text-xl font-semibold text-[var(--color-text)] mb-3">
                Screen Size Too Small
              </h2>
              <p 
                className="text-sm leading-relaxed max-w-md"
                style={{ color: 'color-mix(in srgb, var(--color-text) 60%, transparent)' }}
              >
                This application requires a minimum screen width of 600px for the best experience. 
                Please use a larger screen or switch to a mobile device.
              </p>
            </div>
          </div>
        ) : (
          <ChatPage 
            initialMessage={chatMessage}
            initialTools={chatTools}
            onBackToMain={navigateToMain}
            onHasMessagesChange={handleHasMessagesChange}
          />
        )}
      </div>
      {/* Prompt Library — always rendered so AnimatePresence can play the exit animation */}
      <div
        className="fixed inset-0"
        style={{
          zIndex: 103,
          top: '80px',
          left: isMobile || isTablet ? 0 : `${menuWidth}px`,
          pointerEvents: showPromptLibrary ? 'auto' : 'none',
        }}
      >
        <PromptLibrary
          isOpen={showPromptLibrary}
          onClose={() => setShowPromptLibrary(false)}
          onSelect={handlePromptLibrarySelect}
        />
      </div>
      {/* Confirmation dialog when selecting a prompt while chat is active */}
      <ConfirmationDialog
        isOpen={showPromptConfirmation}
        title="Clear Current Chat?"
        message="Switching to this prompt will clear your current chat history. Would you like to continue?"
        onConfirm={handleConfirmClearChat}
        onCancel={handleCancelPromptSelection}
        confirmText="Clear Chat"
        cancelText="Stay"
      />
      {/* Only show disclaimer on homepage and only for desktop */}
      {currentPage === 'main' && !isMobile && !isTablet && <DisclaimerNote menuWidth={menuWidth} />}
      
      {/* Image Modal — only for regular chat (Ask Chat / 3M Knowledge), not RAG modes */}
      {(!chatModeState.ragChatType) && <ImageModal />}
      
      {/* Jira Card Modal — only for JIRA RAG */}
      {chatModeState.ragChatType === 'JIRA' && <JiraCardModal />}

      {/* Tutorial Components */}
      <WelcomePopup 
        isOpen={showWelcomePopup}
        currentPage={currentPage}
        isPromptLibraryOpen={showPromptLibrary}
        statisticsPanelOpen={showStatsPanel}
      />
      
      <GuidedTutorial />

      {/* Spinner for when the file upload pop up is active */}
      {globalBlocking && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(2px)'
        }}>
            <div style={{ width: 42, height: 42, border: '5px solid rgba(255,255,255,0.25)', borderTop: '5px solid var(--color-accent)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      )}
    </div>
  );
}

// --- Router wrapper ---
// App handles /chat/:ragType internally via useLocation, so all routes render <App />.
// To add a new RAG direct link, add an entry to RAG_OPTIONS in ragConfig.ts.
const AppWithRouter = () => (
  <Router>
    <Routes>
      <Route path="/*" element={<App />} />
    </Routes>
  </Router>
);

export default AppWithRouter;